import type { VoidFunctionComponent } from 'react';
import Badge, { BadgeColors } from '../../../generic/Badge/Badge';
import styles from './RemediationPanels.module.less';
import type { UpgradeDistance } from '../../../../../shared/vulns/types';
import { IMPACT_ASSESSMENT } from '../../../../../shared/featureFlags/flagNames';
import { FeatureFlagRequired } from '../../../generic';
import FixBadge from '../../../issues/IssuesInbox/IssueItem/FixBadge';

const remediationTypes = ['current', 'complete', 'partial', 'no-fix'] as const;
type RemediationType = typeof remediationTypes[number];

const REMEDIATION_NAME: Record<RemediationType, string> = {
  current: 'CURRENT VERSION',
  complete: 'COMPLETE FIX',
  partial: 'PARTIAL FIX',
  'no-fix': 'NO FIX',
};

const REMEDIATION_TEXT: Record<RemediationType, string> = {
  current: 'You are exposed to this vulnerability with this version.',
  complete: 'Nearest update to fix all vulnerabilities found on this dependency.',
  partial: 'Nearest update to fix this vulnerability. This may not remediate all vulnerabilities.',
  'no-fix': 'FOSSA cannot find a fix for this vulnerability at this time.',
};

const REMEDIATION_STYLE: Record<RemediationType, string> = {
  current: styles.remediationSuggestionCurrentVersion,
  complete: styles.remediationSuggestionCompleteFix,
  partial: styles.remediationSuggestionPartialFix,
  'no-fix': styles.remediationSuggestionNoFix,
};

const REMEDIATION_BADGE_COLOR: Record<RemediationType, BadgeColors> = {
  current: BadgeColors.LightRed,
  complete: BadgeColors.DarkGreen,
  partial: BadgeColors.LightGreen,
  'no-fix': BadgeColors.LightRed,
};

type Props = {
  type: RemediationType;
  version?: string;
  upgradeDistance?: UpgradeDistance;
  useFixBadge?: boolean;
};

//  Helper which returns the heading text, depending on the type.
const getHeading = ({ type, version }: Props): `Current version ${string}` | 'No fix available' | `Upgrade to ${string}` => {
  switch (type) {
    case 'current':
      return `Current version ${version}`;
    case 'no-fix':
      return 'No fix available';
    default:
      return `Upgrade to ${version}`;
  }
};

/**
 * PackageVersionVulnerabilityDrawerRemediationSuggestion shows a panel of
 * specific information detailing one of three "fixability" states for a vuln:
 * complete fix, partial fix, or no fix.
 */
const PackageVersionVulnerabilityDrawerRemediationSuggestion: VoidFunctionComponent<Props> = ({
  type,
  version,
  upgradeDistance,
  useFixBadge, // Whether to show the fix badge.  Useful for up until partial fix is enabled
}) => {
  const heading = getHeading({ type, version });
  return (
    <div className={`${styles.remediationSuggestion} ${REMEDIATION_STYLE[type]}`}>
      <div className={styles.remediationSuggestionHeading}>
        <h5>{heading}</h5>
        {type !== 'no-fix' && version && useFixBadge && (
          <FeatureFlagRequired flagName={IMPACT_ASSESSMENT}>
            <FixBadge version={version} distance={upgradeDistance} />
          </FeatureFlagRequired>
        )}
        {type !== 'no-fix' && <Badge color={REMEDIATION_BADGE_COLOR[type]}>{REMEDIATION_NAME[type]}</Badge>}
      </div>
      <p className={styles.remediationSuggestionParagraph}>{REMEDIATION_TEXT[type]}</p>
    </div>
  );
};

export default PackageVersionVulnerabilityDrawerRemediationSuggestion;
